import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { directLoginUrl } from "@/lib/format";
import { X, Copy, Check, Eye, EyeOff, ExternalLink, ShieldAlert, Clock } from "lucide-react";

/**
 * Technischer NCAdmin-Credentials-Reveal (nur Superadmin, nur Souvera-Clouds).
 *
 *   * Ruft beim Öffnen `GET /api/admin/clouds/{id}/credentials/ncadmin` auf —
 *     das Backend AKTIVIERT dabei den technischen `ncadmin` per occ für ein
 *     30-Minuten-Fenster (danach schaltet ein Worker ihn wieder ab + synct das
 *     System-Adressbuch).
 *   * Zeigt einen LIVE-Countdown des 30-Min-Fensters (`active_until`) — KEIN
 *     30s-Auto-Close wie beim normalen Credentials-Modal, da hier das
 *     Aktiv-Fenster die relevante Zeitspanne ist.
 *   * Deutlicher Warnhinweis: technischer Admin, NICHT an den Kunden geben.
 */
export default function NCAdminCredentialsModal({ cloudId, url, onClose }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pwVisible, setPwVisible] = useState(false);
    const [copied, setCopied] = useState(null);
    const [secsLeft, setSecsLeft] = useState(null);

    // Initial-Fetch — jeder Mount → Backend re-aktiviert ncadmin + stempelt
    // ein frisches 30-Min-Fenster.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/api/admin/clouds/${cloudId}/credentials/ncadmin`);
                if (cancelled) return;
                setData(r.data);
                setLoading(false);
            } catch (e) {
                if (cancelled) return;
                setErr(formatApiError(e));
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [cloudId]);

    // Live-Countdown des 30-Min-Aktiv-Fensters (aus `active_until`).
    useEffect(() => {
        if (!data?.active_until) return;
        const deadline = new Date(data.active_until).getTime();
        const tick = () => setSecsLeft(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
        tick();
        const iv = setInterval(tick, 1000);
        return () => clearInterval(iv);
    }, [data]);

    // Escape zum Schließen
    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const copy = async (label, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(null), 1500);
        } catch (e) {
            // Ignorieren – kein Clipboard in unsicheren Kontexten (HTTP local dev)
        }
    };

    const fmtWindow = () => {
        if (secsLeft == null) return "—";
        const m = Math.floor(secsLeft / 60);
        const s = secsLeft % 60;
        return `${m}m ${String(s).padStart(2, "0")}s`;
    };
    const untilClock = data?.active_until
        ? new Date(data.active_until).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
        : null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="ncadmin-credentials-modal-overlay"
        >
            <div
                className="bg-zinc-950 border border-red-800/60 max-w-lg w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="ncadmin-credentials-modal"
            >
                <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <ShieldAlert size={16} className="text-red-400" />
                        <h2 className="cm-h3">Technical NCAdmin credentials</h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="ncadmin-credentials-close-button"
                        className="text-zinc-400 hover:text-zinc-200"
                        aria-label="close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 text-sm">
                    {loading && (
                        <div className="text-zinc-400" data-testid="ncadmin-credentials-loading">
                            Activating NCAdmin & loading credentials…
                        </div>
                    )}

                    {err && (
                        <div
                            className="bg-[#3a1010] border border-[#7a2a2a] p-3 text-xs text-[#ffaaaa] font-mono"
                            data-testid="ncadmin-credentials-error"
                        >
                            {err}
                        </div>
                    )}

                    {data && (
                        <>
                            <div className="bg-red-900/25 border border-red-700/60 p-3 text-xs text-red-200 leading-relaxed">
                                <strong>Technical Host/Souvera admin</strong> —{" "}
                                <strong>do NOT</strong> give to the customer. The user was activated for{" "}
                                <span className="font-mono text-red-100">
                                    {data.window_minutes || 30} minutes
                                </span>{" "}
                                and is automatically deactivated afterwards (incl.
                                address-book sync).
                            </div>

                            <div
                                className="flex items-center gap-2 bg-amber-900/15 border border-amber-700/40 px-3 py-2 text-xs text-amber-200"
                                data-testid="ncadmin-credentials-window"
                            >
                                <Clock size={13} className="text-amber-300" />
                                {secsLeft === 0 ? (
                                    <span>Window expired — ncadmin is automatically deactivated.</span>
                                ) : (
                                    <span>
                                        Active until <span className="font-mono text-amber-100">{untilClock}</span>
                                        {" · "}<span className="font-mono text-amber-100">{fmtWindow()}</span> left
                                    </span>
                                )}
                            </div>

                            {url && (
                                <CredRow
                                    testidBase="ncadmin-credentials-url"
                                    label="URL"
                                    value={url}
                                    copy={() => copy("url", url)}
                                    copied={copied === "url"}
                                    trailing={
                                        <a
                                            href={directLoginUrl(url)}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            className="text-cm-brand hover:text-cm-brand"
                                            title="Open local login (bypasses external auth)"
                                        >
                                            <ExternalLink size={13} />
                                        </a>
                                    }
                                />
                            )}

                            <CredRow
                                testidBase="ncadmin-credentials-username"
                                label="NCAdmin User"
                                value={data.admin_user}
                                copy={() => copy("user", data.admin_user)}
                                copied={copied === "user"}
                            />

                            <CredRow
                                testidBase="ncadmin-credentials-password"
                                label="NCAdmin password"
                                value={
                                    data.admin_password == null
                                        ? "(not decryptable)"
                                        : pwVisible
                                            ? data.admin_password
                                            : "•".repeat(data.admin_password.length)
                                }
                                copy={data.admin_password == null ? undefined : () => copy("password", data.admin_password)}
                                copied={copied === "password"}
                                trailing={
                                    data.admin_password == null ? null : (
                                        <button
                                            type="button"
                                            onClick={() => setPwVisible((v) => !v)}
                                            data-testid="ncadmin-credentials-password-toggle"
                                            className="text-zinc-400 hover:text-zinc-100"
                                            title={pwVisible ? "hide" : "show"}
                                        >
                                            {pwVisible ? <EyeOff size={13} /> : <Eye size={13} />}
                                        </button>
                                    )
                                }
                            />

                            <div className="text-[11px] text-zinc-500 leading-relaxed pt-2 border-t border-zinc-800">
                                {data.note}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

function CredRow({ label, value, copy, copied, trailing, testidBase }) {
    return (
        <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
            <div
                className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 py-2"
                data-testid={testidBase}
            >
                <span
                    className="flex-1 font-mono text-xs text-zinc-100 break-all"
                    data-testid={`${testidBase}-value`}
                >
                    {value}
                </span>
                <button
                    type="button"
                    onClick={copy}
                    data-testid={`${testidBase}-copy-button`}
                    className="text-zinc-400 hover:text-zinc-100"
                    title="copy"
                >
                    {copied ? (
                        <Check size={13} className="text-emerald-400" />
                    ) : (
                        <Copy size={13} />
                    )}
                </button>
                {trailing}
            </div>
        </div>
    );
}
